import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGameStore } from '@/store/gameStore';
import { GameBoard } from '@/components/GameBoard';
import { HUD } from '@/components/HUD';
import { Settings, SettingsButton } from '@/components/Settings';
import { Tutorial } from '@/components/Tutorial';
import { ChapterIntro } from '@/components/ChapterIntro';
import { calculateScore } from '@/engine/score';
import { calculateStars } from '@/engine/stars';
import { generateShareCard, formatShareText, shareResult } from '@/engine/share';
import type { Score } from '@/types';

function parsePuzzleChapter(puzzleId: string): number {
  const match = puzzleId.match(/ch(\d+)/);
  return match?.[1] ? parseInt(match[1], 10) : 1;
}

export function GameScreen() {
  const puzzle = useGameStore((s) => s.puzzle);
  const board = useGameStore((s) => s.board);
  const isComplete = useGameStore((s) => s.isComplete);
  const isLost = useGameStore((s) => s.isLost);
  const strikesUsed = useGameStore((s) => s.strikesUsed);
  const strikeBudget = useGameStore((s) => s.strikeBudget);
  const undoUsedThisPuzzle = useGameStore((s) => s.undoUsedThisPuzzle);
  const resetsUsed = useGameStore((s) => s.resetsUsed);
  const startTime = useGameStore((s) => s.startTime);
  const navigateTo = useGameStore((s) => s.navigateTo);
  const reset = useGameStore((s) => s.reset);
  const loadPuzzle = useGameStore((s) => s.loadPuzzle);
  const recordSolve = useGameStore((s) => s.recordSolve);
  const lastActionAt = useGameStore((s) => s.lastActionAt);
  const hintLevel = useGameStore((s) => s.hintLevel);
  const requestHint = useGameStore((s) => s.requestHint);
  const showTutorial = useGameStore((s) => s.showTutorial);
  const tutorialComplete = useGameStore((s) => s.tutorialComplete);
  const showChapterIntro = useGameStore((s) => s.showChapterIntro);
  const chapterIntroComplete = useGameStore((s) => s.chapterIntroComplete);

  // Share feedback toast state
  const [shareFeedback, setShareFeedback] = useState<string | null>(null);

  // Hint availability timer
  const [hintAvailable, setHintAvailable] = useState(false);

  useEffect(() => {
    if (isComplete || isLost) {
      setHintAvailable(false);
      return;
    }
    const timer = setInterval(() => {
      const elapsed = Date.now() - lastActionAt;
      setHintAvailable(elapsed > 60000);
    }, 1000);
    return () => clearInterval(timer);
  }, [lastActionAt, isComplete, isLost]);

  const handleBackClick = useCallback(() => {
    navigateTo('puzzleSelect');
  }, [navigateTo]);

  const handleNextPuzzle = useCallback(() => {
    // For now, go back to puzzle select
    navigateTo('puzzleSelect');
  }, [navigateTo]);

  const handleTryAgain = useCallback(() => {
    reset();
  }, [reset]);

  const handleBackToMenu = useCallback(() => {
    navigateTo('menu');
  }, [navigateTo]);

  const handleReplay = useCallback(() => {
    if (puzzle) {
      loadPuzzle(puzzle);
    }
  }, [puzzle, loadPuzzle]);

  // Calculate score and stars for win overlay
  const { score, stars } = useMemo(() => {
    if (!isComplete || !puzzle) {
      return { score: null as Score | null, stars: 0 };
    }
    const solveTimeSeconds = Math.round((Date.now() - startTime) / 1000);
    const targetTimeSeconds = 120; // Default target time

    const scoreResult = calculateScore({
      tier: puzzle.tier,
      strikesUsed,
      strikeBudget,
      undoUsed: undoUsedThisPuzzle,
      solveTimeSeconds,
      targetTimeSeconds,
      resetsUsed,
    });

    const starCount = calculateStars(
      true,
      strikesUsed,
      puzzle.constraints.minimumStrikes,
      undoUsedThisPuzzle,
      resetsUsed,
    );

    return { score: scoreResult, stars: starCount };
  }, [isComplete, puzzle, startTime, strikesUsed, strikeBudget, undoUsedThisPuzzle, resetsUsed]);

  const handleShare = useCallback(async () => {
    if (!puzzle || !board) return;
    const card = generateShareCard(
      puzzle.id, puzzle.metadata.title, stars,
      strikesUsed, strikeBudget, resetsUsed,
      Math.round((Date.now() - startTime) / 1000),
      board,
    );
    const text = formatShareText(card);
    const result = await shareResult(text);
    setShareFeedback(result === 'copied' ? 'Copied to clipboard!' : result === 'shared' ? 'Shared!' : 'Could not share');
    setTimeout(() => setShareFeedback(null), 2000);
  }, [puzzle, board, stars, strikesUsed, strikeBudget, resetsUsed, startTime]);

  // Delay the win overlay so the celebration animation plays first (2.5s per PRD)
  const [showWinOverlay, setShowWinOverlay] = useState(false);
  const winTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-save progress when puzzle is completed
  const recordSolveCalled = useRef(false);
  useEffect(() => {
    if (isComplete && !recordSolveCalled.current) {
      recordSolveCalled.current = true;
      recordSolve();
    }
    if (!isComplete) {
      recordSolveCalled.current = false;
    }
  }, [isComplete, recordSolve]);

  useEffect(() => {
    if (isComplete) {
      // Wait for propagation animation (~1s) + celebration time (1.5s)
      winTimerRef.current = setTimeout(() => setShowWinOverlay(true), 2500);
    } else {
      setShowWinOverlay(false);
      if (winTimerRef.current) {
        clearTimeout(winTimerRef.current);
        winTimerRef.current = null;
      }
    }
    return () => {
      if (winTimerRef.current) clearTimeout(winTimerRef.current);
    };
  }, [isComplete]);

  if (!puzzle) return null;

  return (
    <div style={containerStyle}>
      {/* Top bar */}
      <div style={topBarStyle}>
        <button
          style={backButtonStyle}
          onClick={handleBackClick}
          aria-label="Back to puzzle select"
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
          }}
        >
          &#8592;
        </button>
        <div style={topBarInfoStyle}>
          <span style={puzzleTitleStyle}>{puzzle.metadata.title}</span>
          <span style={puzzleTierStyle}>Tier {puzzle.tier}</span>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <SettingsButton />
        </div>
      </div>

      {/* Settings overlay */}
      <Settings />

      {/* Game board area */}
      <div style={boardAreaStyle}>
        <GameBoard
          hintHighlightNodeId={hintLevel >= 1 ? (useGameStore.getState().hintNodeIds[0] ?? null) : null}
          hintSequence={hintLevel >= 2 ? useGameStore.getState().hintNodeIds : []}
        />
      </div>

      {/* HUD */}
      <HUD
        hintAvailable={hintAvailable}
        hintLevel={hintLevel}
        onRequestHint={requestHint}
      />

      {/* Win overlay -- delayed to let celebration animation play */}
      {showWinOverlay && score && (
        <div style={overlayBackdropStyle} role="dialog" aria-label="Puzzle completed">
          {/* Radial glow behind card */}
          <div style={overlayGlowStyle} />
          <div style={overlayCardStyle}>
            <h2 style={winTitleStyle}>Resonance Achieved</h2>

            <div style={starDisplayStyle} aria-label={`${stars} of 3 stars`}>
              {renderLargeStars(stars)}
            </div>

            <div style={scoreBreakdownStyle}>
              <ScoreRow label="Base" value={score.base} />
              <ScoreRow label="Strike Bonus" value={score.strikeBonus} />
              <ScoreRow label="Undo Bonus" value={score.undoBonus} />
              <ScoreRow label="Time Bonus" value={score.timeBonus} />
              {score.resetPenalty !== 0 && (
                <ScoreRow label="Reset Penalty" value={score.resetPenalty} negative />
              )}
              <div style={scoreDividerStyle} />
              <ScoreRow label="Total" value={score.total} bold />
            </div>

            <div style={overlayButtonsStyle}>
              <button
                style={primaryButtonStyle}
                onClick={handleNextPuzzle}
                aria-label="Go to next puzzle"
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(114, 9, 183, 0.4)';
                  e.currentTarget.style.boxShadow = '0 0 20px rgba(114, 9, 183, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(114, 9, 183, 0.25)';
                  e.currentTarget.style.boxShadow = '0 0 12px rgba(114, 9, 183, 0.15)';
                }}
              >
                Next Puzzle
              </button>
              <button
                style={shareButtonStyle}
                onClick={handleShare}
                aria-label="Share your result"
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(27, 154, 170, 0.3)';
                  e.currentTarget.style.boxShadow = '0 0 20px rgba(27, 154, 170, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(27, 154, 170, 0.15)';
                  e.currentTarget.style.boxShadow = '0 0 12px rgba(27, 154, 170, 0.1)';
                }}
              >
                {shareFeedback ?? 'Share Result'}
              </button>
              <button
                style={secondaryButtonStyle}
                onClick={handleReplay}
                aria-label="Replay this puzzle"
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                }}
              >
                Replay
              </button>
              <button
                style={secondaryButtonStyle}
                onClick={handleBackToMenu}
                aria-label="Return to main menu"
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                }}
              >
                Back to Menu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share feedback toast */}
      {shareFeedback && !showWinOverlay && (
        <div style={toastStyle} role="status" aria-live="polite">
          {shareFeedback}
        </div>
      )}

      {/* Onboarding tutorial overlay */}
      {showTutorial && (
        <Tutorial onComplete={tutorialComplete} />
      )}

      {/* Chapter mechanic introduction overlay */}
      {!showTutorial && showChapterIntro && puzzle && (
        <ChapterIntro
          chapter={parsePuzzleChapter(puzzle.id)}
          onComplete={chapterIntroComplete}
        />
      )}

      {/* Loss overlay */}
      {isLost && (
        <div style={overlayBackdropStyle} role="dialog" aria-label="Puzzle failed">
          <div style={overlayCardStyle}>
            <h2 style={lossTitleStyle}>Strikes Exhausted</h2>

            <p style={lossMessageStyle}>
              All strikes have been used without achieving resonance.
            </p>

            <div style={overlayButtonsStyle}>
              <button
                style={primaryButtonStyle}
                onClick={handleTryAgain}
                aria-label="Try this puzzle again"
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(114, 9, 183, 0.4)';
                  e.currentTarget.style.boxShadow = '0 0 20px rgba(114, 9, 183, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(114, 9, 183, 0.25)';
                  e.currentTarget.style.boxShadow = '0 0 12px rgba(114, 9, 183, 0.15)';
                }}
              >
                Try Again
              </button>
              <button
                style={secondaryButtonStyle}
                onClick={handleBackToMenu}
                aria-label="Return to main menu"
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                }}
              >
                Back to Menu
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Score row sub-component ──

function ScoreRow(props: { label: string; value: number; negative?: boolean; bold?: boolean }) {
  const valueColor = props.negative
    ? '#FF6B6B'
    : props.value > 0
      ? '#e8e8e4'
      : 'rgba(232,232,228,0.4)';
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '4px 0',
      }}
    >
      <span
        style={{
          fontSize: props.bold ? '15px' : '13px',
          fontWeight: props.bold ? 600 : 400,
          color: 'rgba(232,232,228,0.6)',
        }}
      >
        {props.label}
      </span>
      <span
        style={{
          fontSize: props.bold ? '17px' : '14px',
          fontWeight: props.bold ? 700 : 500,
          color: valueColor,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {props.value > 0 && !props.negative ? '+' : ''}
        {props.value}
      </span>
    </div>
  );
}

// ── Star rendering with golden glow ──

function renderLargeStars(count: number): React.ReactNode {
  const stars: React.ReactNode[] = [];
  for (let i = 0; i < 3; i++) {
    const filled = i < count;
    stars.push(
      <span
        key={i}
        style={{
          fontSize: '40px',
          color: filled ? '#FCBF49' : 'rgba(252, 191, 73, 0.15)',
          textShadow: filled
            ? '0 0 10px rgba(252, 191, 73, 0.6), 0 0 25px rgba(252, 191, 73, 0.4), 0 0 45px rgba(252, 191, 73, 0.15)'
            : 'none',
          transition: 'all 0.3s ease',
          animationDelay: `${i * 200}ms`,
          filter: filled ? 'drop-shadow(0 0 4px rgba(252, 191, 73, 0.5))' : 'none',
        }}
      >
        {'\u2605'}
      </span>,
    );
  }
  return stars;
}

// ── Styles ──

const containerStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: '#050510',
  position: 'relative',
  overflow: 'hidden',
};

const topBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '10px 16px',
  borderBottom: '1px solid rgba(255,255,255,0.04)',
  flexShrink: 0,
  background: 'rgba(10, 10, 30, 0.5)',
};

const backButtonStyle: React.CSSProperties = {
  width: '36px',
  height: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255,255,255,0.05)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '8px',
  color: '#e8e8e4',
  fontSize: '18px',
  cursor: 'pointer',
  transition: 'background 0.2s ease',
  flexShrink: 0,
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  outline: 'none',
};

const topBarInfoStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: '12px',
};

const puzzleTitleStyle: React.CSSProperties = {
  fontSize: '16px',
  fontWeight: 400,
  color: '#e8e8e4',
  letterSpacing: '0.04em',
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
};

const puzzleTierStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 400,
  color: 'rgba(199, 125, 255, 0.5)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
};

const boardAreaStyle: React.CSSProperties = {
  flex: 1,
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
};

const overlayBackdropStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  background: 'rgba(5, 5, 16, 0.88)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 100,
};

const overlayGlowStyle: React.CSSProperties = {
  position: 'absolute',
  width: '500px',
  height: '500px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(114, 9, 183, 0.12) 0%, rgba(199, 125, 255, 0.05) 40%, transparent 70%)',
  pointerEvents: 'none',
};

const overlayCardStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.05)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '20px',
  padding: '40px 36px',
  maxWidth: '380px',
  width: '90%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '24px',
  position: 'relative',
  zIndex: 1,
  boxShadow: '0 0 60px rgba(114, 9, 183, 0.1), inset 0 0 30px rgba(114, 9, 183, 0.03)',
};

const winTitleStyle: React.CSSProperties = {
  fontSize: '24px',
  fontWeight: 300,
  color: '#e8e8e4',
  letterSpacing: '0.12em',
  textShadow: '0 0 20px rgba(199, 125, 255, 0.5), 0 0 40px rgba(114, 9, 183, 0.3)',
  margin: 0,
  textAlign: 'center',
  fontFamily: "'Cormorant Garamond', 'Palatino', 'Georgia', serif",
};

const lossTitleStyle: React.CSSProperties = {
  fontSize: '24px',
  fontWeight: 300,
  color: '#FF6B6B',
  letterSpacing: '0.08em',
  textShadow: '0 0 20px rgba(255, 107, 107, 0.3)',
  margin: 0,
  textAlign: 'center',
  fontFamily: "'Cormorant Garamond', 'Palatino', 'Georgia', serif",
};

const lossMessageStyle: React.CSSProperties = {
  fontSize: '14px',
  color: 'rgba(232,232,228,0.5)',
  textAlign: 'center',
  lineHeight: 1.5,
  margin: 0,
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
};

const starDisplayStyle: React.CSSProperties = {
  display: 'flex',
  gap: '12px',
};

const scoreBreakdownStyle: React.CSSProperties = {
  width: '100%',
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
};

const scoreDividerStyle: React.CSSProperties = {
  height: '1px',
  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
  margin: '8px 0',
};

const overlayButtonsStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  width: '100%',
  marginTop: '8px',
};

const primaryButtonStyle: React.CSSProperties = {
  padding: '14px 24px',
  background: 'rgba(114, 9, 183, 0.25)',
  border: '1px solid rgba(114, 9, 183, 0.4)',
  borderRadius: '10px',
  color: '#e8e8e4',
  fontSize: '15px',
  fontWeight: 500,
  letterSpacing: '0.06em',
  cursor: 'pointer',
  transition: 'all 0.25s ease',
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  outline: 'none',
  boxShadow: '0 0 12px rgba(114, 9, 183, 0.15)',
};

const shareButtonStyle: React.CSSProperties = {
  padding: '13px 24px',
  background: 'rgba(27, 154, 170, 0.15)',
  border: '1px solid rgba(27, 154, 170, 0.35)',
  borderRadius: '10px',
  color: '#e8e8e4',
  fontSize: '14px',
  fontWeight: 500,
  letterSpacing: '0.06em',
  cursor: 'pointer',
  transition: 'all 0.25s ease',
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  outline: 'none',
  boxShadow: '0 0 12px rgba(27, 154, 170, 0.1)',
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: '12px 24px',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '10px',
  color: 'rgba(232,232,228,0.7)',
  fontSize: '14px',
  fontWeight: 400,
  letterSpacing: '0.04em',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  outline: 'none',
};

const toastStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: '100px',
  left: '50%',
  transform: 'translateX(-50%)',
  background: 'rgba(27, 154, 170, 0.9)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  color: '#fff',
  padding: '10px 24px',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 500,
  letterSpacing: '0.04em',
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  zIndex: 200,
  pointerEvents: 'none',
  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
};
